import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { KeywordStatsRecompute } from '@brand24-original/llm-types';
import { getKeywordStatsRecompute } from '../../../../services/monitor/keywords/KeywordsService';
import { GET_KEYWORD_STATS_RECOMPUTE } from '../../../query/keyword/keys';
import { invalidateBrandStats } from '../../../query/invalidateBrandStats';

const POLL_MS = 5_000;

/**
 * Które marki projektu mają statystyki w trakcie przeliczania (po włączeniu nazwy worker liczy je ok. minuty).
 * Dopóki lista nie jest pusta, odpytuje co 5 s; gdy się opróżni, unieważnia wszystkie statystyki, żeby
 * tabele przeszły z loaderów na świeże liczby bez odświeżania strony. Sam wpis na listę pojawia się od razu,
 * bo `invalidateBrandStats` po przełączeniu marki odświeża także to zapytanie.
 */
export const useKeywordStatsRecompute = (projectId: number | null) => {
  const queryClient = useQueryClient();
  const previousCount = useRef(0);

  const { data } = useQuery<KeywordStatsRecompute[]>({
    queryKey: [GET_KEYWORD_STATS_RECOMPUTE, projectId],
    queryFn: () => getKeywordStatsRecompute(projectId!),
    enabled: !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    // Z danych zapytania, nie z refa: ref zmienia się w efekcie, bez renderu, więc interwał by nie ruszył.
    refetchInterval: (query) => (query.state.data?.length ? POLL_MS : false),
  });

  const pending = data ?? [];
  const pendingBrands = useMemo(
    () => Array.from(new Set(pending.map((entry) => entry.normalizedKeyword))),
    [pending]
  );

  // Dashboard czeka z loaderami tylko na markę wybraną projektu; inne marki dociągną świeże liczby po końcu.
  const selectedBrandPending = useMemo(
    () =>
      Array.from(
        new Set(pending.filter((entry) => entry.isSelectedBrand).map((entry) => entry.normalizedKeyword))
      ),
    [pending]
  );

  useEffect(() => {
    if (previousCount.current > 0 && pendingBrands.length === 0) {
      void invalidateBrandStats(queryClient);
    }
    previousCount.current = pendingBrands.length;
  }, [pendingBrands, queryClient]);

  return {
    pendingBrands,
    isRecomputing: pendingBrands.length > 0,
    selectedBrandPending,
    isSelectedBrandRecomputing: selectedBrandPending.length > 0,
  };
};
